import { Injectable } from '@angular/core';


@Injectable({
    providedIn: 'root',
})
export class EncryptionService {
    private encoder = new TextEncoder();
    private decoder = new TextDecoder();
    private keyCache = new Map<string, CryptoKey>();
    SESSION_ENCRYPTION_KEY: '22AED7C7-9C6D-4B1C-A14F-623CD6C3589B';
    
    API_AGENT_ORIGIN: 'https://agent.finobooks.com/';
    API_AGENT_BASE_URL: 'https://agent.finobooks.com/api';
    /** ------------------- PUBLIC API ------------------- */

    /**
     * Encrypts a payload and stores it in sessionStorage
     * @param environmentKey - key used for encryption
     * @param payload - data to encrypt
     * @param sessionStorageKey - key for sessionStorage
     */
    
    async encryptAsync(
        payload: any,
        sessionStorageKey: string
    ): Promise<void> {
        const cryptoKey = await this.getCryptoKey(this.SESSION_ENCRYPTION_KEY);
        const iv = crypto.getRandomValues(new Uint8Array(12));

        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            cryptoKey,
            this.encoder.encode(JSON.stringify(payload))
        );

        const encryptedData = {
            iv: this.toBase64(iv),
            data: this.toBase64(encrypted),
        };

        sessionStorage.setItem(sessionStorageKey, JSON.stringify(encryptedData));
    }

    /**
     * Decrypts data from sessionStorage
     * @param environmentKey - key used for decryption
     * @param sessionStorageKey - key in sessionStorage
     */
    async decryptAsync<T>(sessionStorageKey: string): Promise<T | null> {
        const stored = sessionStorage.getItem(sessionStorageKey);
        if (!stored) return null;
      
        try {
          const { iv, data } = JSON.parse(stored);
          const cryptoKey = await this.getCryptoKey(this.SESSION_ENCRYPTION_KEY);
      
          const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv: this.fromBase64(iv) },
            cryptoKey,
            this.fromBase64(data)
          );
      
          return JSON.parse(this.decoder.decode(decrypted)) as T;
        } catch (err) {
          console.error('Decryption failed:', err);
          return null;
        }
      }


    clear(sessionStorageKey: string): void {
        sessionStorage.removeItem(sessionStorageKey);
    }

    clearAll(): void {
        Object.values(session_storage_keys).forEach(key => {
            sessionStorage.removeItem(key);
        });
    }
    

    /** ------------------- PRIVATE HELPERS ------------------- */

    private async getCryptoKey(environmentKey: string): Promise<CryptoKey> {
        if (this.keyCache.has(environmentKey)) {
            return this.keyCache.get(environmentKey)!;
        }

        const keyMaterial = await crypto.subtle.importKey(
            'raw',
            this.encoder.encode(environmentKey),
            'PBKDF2',
            false,
            ['deriveKey']
        );

        const cryptoKey = await crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt: this.encoder.encode('angular-session-salt'),
                iterations: 100_000,
                hash: 'SHA-256',
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            false,
            ['encrypt', 'decrypt']
        );

        this.keyCache.set(environmentKey, cryptoKey);
        return cryptoKey;
    }

    private toBase64(buffer: ArrayBuffer | Uint8Array): string {
        const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
        return btoa(String.fromCharCode(...bytes));
    }

    fromBase64(base64: string): ArrayBuffer {
        const binary = atob(base64);
        const len = binary.length;
        const buffer = new ArrayBuffer(len);
        const bytes = new Uint8Array(buffer);
      
        for (let i = 0; i < len; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
      
        return buffer;
      }


    private authMemory = new Map<string, string>();

    /* ================= MEMORY STORE ================= */

    setAuthValue(key: string, value: string): void {
        this.authMemory.set(key, value);
        console.log("setAuthValue: authMemory", this.authMemory)
    }

    getAuthValue(key: string): string | undefined {
        console.log("getAuthValue: authMemory", this.authMemory)

        return this.authMemory.get(key);
    }

    clearAuthMemory(): void {
        this.authMemory.clear();
    }


}

export const session_storage_keys = {
    USER_SESSION: 'USER_SESSION',
    AUTH_TOKEN: 'AUTH_TOKEN',
    SETTINGS: 'APP_SETTINGS',
    PROFILE: 'USER_PROFILE',
    DEFAULT_TOKEN: 'DEFAULT_TOKEN',
    DEFAULT_USER_NAME: 'DEFAULT_USER_NAME',
    USER_NAME: 'USER_NAME',
    
    DEFAULT_PASSWORD: 'DEFAULT_PASSWORD',
    DEFAULT_URL: 'DEFAULT_URL',
    DEFAULT_MASTER_ID: 'DEFAULT_MASTER_ID',
    DEFAULT_PASS_KEY: 'DEFAULT_PASS_KEY',
    DEFAULT_MASTER_ROLE_ID: 'DEFAULT_MASTER_ROLE_ID',
    DEFAULT_COMPANY_ID: 'DEFAULT_COMPANY_ID',
    DEFAULT_SUBSCRIPTION_ID: 'DEFAULT_SUBSCRIPTION_ID',
    PRIME_AUTH_TOKEN:'PRIME_AUTH_TOKEN',
    PRIME_AUTH_PASSKEY:'PRIME_AUTH_PASSKEY',
    PRIME_MASTER_ID:'PRIME_MASTER_ID',
    CURRENT_USER_ENVIRONMENT: 'CURRENT_USER_ENVIRONMENT',

    MERGED_MODULES_LIST: 'MERGED_MODULES_LIST',
    REG_MERGED_MODULES_LIST: 'REG_MERGED_MODULES_LIST',
    COMPANY_LIST: 'COMPANY_LIST',
    SUBSCRIBE_LIST: 'SUBSCRIBE_LIST',
    STANDARD_REPORT: 'STANDARD_REPORT',
   USER_PROFILE: 'USER_PROFILE',


    API_GEN_ORIGIN: 'FIN070514-25EF',
    API_GEN_ROLE_ORIGIN: 'AD211905-25EF',
    API_GEN_UPLOAD_ORIGIN: 'AD060912-25EF',

    API_INV_ORIGIN: 'LI050307150910_25EF',
    API_INV_PUR_ORIGIN: 'LI090325061509_25EF',
    API_INV_STOCK_ORIGIN: 'LI241116222415_25EF',
    API_INV_SALES_ORIGIN: 'LI262020060714_25EF',

    API_PROJ_ORIGIN: 'LI090118181615_25EF',
    API_PROJ_PUR_ORIGIN: 'LI010502220423_25EF',
    API_PROJ_WORKSHOP_ORIGIN: 'LI220709101809_25EF',
    API_PROJ_STOCK_ORIGIN: 'LI251119171626_25EF',
    API_PROJ_WORK_ORIGIN: 'LI090307101124_25EF',
    API_PROJ_MINING_ORIGIN: 'LI131209092110_25EF',

    API_ACC_ORIGIN: 'LI062023210426_25EF',
// ///////added by sg////////////////
//     API_UPLOAD_ORIGIN: 'LI211223170710_25EF',

};

